import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import events from '../data/events';
import { setPageMeta } from '../utils/seo';

/**
 * HomePage - P.S. 155 William Paca School Landing Page
 * Unique, sleek & professional design catered to a school look.
 * Colors: #fcbe37 (Gold) & #296481 (Blue)
 */
const HomePage = () => {
  useEffect(() => {
    setPageMeta({
      title: 'PS 155 William Paca School — Dual Language Elementary & Community School in Harlem',
      description: 'P.S. 155 William Paca is a dual language elementary school and community school in East Harlem, offering Spanish-English bilingual education for Pre-K through Grade 5.',
    });
  }, []);
  return (
    <div className="min-h-screen bg-neutral-50 font-sans text-neutral-800 selection:bg-school-gold selection:text-white">
      <Navbar />

      <main id="main-content" tabIndex="-1" className="outline-none">
        <HeroSection />
        <QuickLinksSection />
        <AboutSection />
        <StatsSection />
        <NewsEventsSection />
        <InstagramSection />
        <CallToActionSection />
      </main>

      <Footer />
    </div>
  );
};

/* ============================================
   HERO SECTION
   ============================================ */
const HeroSection = () => {
  return (
    <section className="relative flex h-[100svh] min-h-[560px] w-full flex-col overflow-hidden bg-school-blue-dark">
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
          tabIndex={-1}
          poster="https://3.files.edl.io/6f24/22/03/22/161414-a9b6960d-de73-4da2-864f-5230fbe390cd.JPG"
          className="w-full h-full object-cover rotate-[1deg] scale-110"
        >
          <source src="/videos/hero-bg.mp4" type="video/mp4" />
          <source src="/videos/hero-bg.webm" type="video/webm" />
        </video>
        {/* Gentle top/bottom vignette only. No text sits on the footage any
            more, so it no longer needs a heavy tint. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-school-blue-dark/40 via-transparent to-school-blue-dark/40"
        />
      </div>

      {/* Lower-third band: the hero copy sits on a slim, SOLID navy strip
          pinned to the bottom of the hero so the video stays unobstructed
          above it. The backing is opaque on purpose: axe cannot resolve a
          contrast ratio for text over a <video>, so every line here needs a
          flat surface to stay decidable. */}
      <div className="relative z-10 mt-auto w-full border-t-2 border-school-gold bg-school-blue-dark">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-6 py-3.5 md:px-10 md:py-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
          <div className="min-w-0">
            {/* The welcome badge stays in the accessibility tree; visually
                the band carries only the title and the motto. */}
            <p className="sr-only">Welcome to East Harlem's Hidden Gem</p>
            <h1 className="text-xl font-extrabold leading-tight tracking-tight text-white md:text-2xl lg:text-3xl">
              <span className="whitespace-nowrap">P.S. 155</span>
            </h1>
            <p className="mt-0.5 text-xs font-semibold text-school-gold md:text-sm">
              Where Panthers Grow With Passion, Purpose and East Harlem Pride
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-3 lg:shrink-0">
            <a
              href="#visit"
              className="inline-flex items-center justify-center rounded-full bg-school-gold px-3 py-2.5 text-center text-xs font-bold uppercase tracking-[0.06em] text-school-blue-dark transition-colors duration-300 hover:bg-yellow-500 sm:px-4 sm:tracking-[0.14em]"
            >
              Schedule a Visit
            </a>
            <a
              href="/about"
              className="inline-flex items-center justify-center rounded-full border-2 border-white px-3 py-2 text-center text-xs font-bold uppercase tracking-[0.06em] text-white transition-colors duration-300 hover:bg-white hover:text-school-blue sm:px-4 sm:tracking-[0.14em]"
            >
              Learn More About P.S. 155
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================
   QUICK LINKS (Floating Dock)
   ============================================ */
const QuickLinksSection = () => {
  const links = [
    {
      label: 'Calendar',
      href: '/calendar',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
        </svg>
      )
    },
    {
      label: 'Parent Portal',
      href: '#parents',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
      )
    },
    {
      label: 'Staff Directory',
      href: '#staff',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      )
    },
    {
      label: 'School Meals',
      href: '#meals',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
        </svg>
      )
    },
    {
      label: 'Contact',
      href: '/contact',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
        </svg>
      )
    },
  ];

  return (
    <section className="relative z-20 pt-10 sm:pt-14 px-4 mb-16 sm:mb-24">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border-t-4 sm:border-t-8 border-school-gold">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-6 md:gap-8">
          {links.map((link, idx) => (
            <a
              key={idx}
              href={link.href}
              className="flex flex-col items-center justify-center gap-2 sm:gap-3 md:gap-4 p-3 sm:p-4 rounded-xl hover:bg-neutral-50 transition-all duration-300 group"
            >
              <span className="text-school-blue group-hover:text-school-gold-dark group-hover:scale-110 transition-all duration-300 [&>svg]:w-6 [&>svg]:h-6 sm:[&>svg]:w-7 sm:[&>svg]:h-7 md:[&>svg]:w-8 md:[&>svg]:h-8">
                {link.icon}
              </span>
              <span className="font-semibold text-school-blue group-hover:text-school-gold-dark transition-colors text-xs sm:text-sm md:text-base text-center leading-tight">
                {link.label}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================
   ABOUT SECTION
   ============================================ */
const AboutSection = () => {
  return (
    <section id="about" className="py-16 sm:py-20 md:py-24 px-4 overflow-hidden bg-neutral-50">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
        <div className="relative order-2 lg:order-1">
          <div className="absolute -top-10 -left-10 w-64 h-64 bg-school-gold/10 rounded-full blur-3xl" />
          <div className="relative z-10">
            <img
              src="https://3.files.edl.io/66a7/25/03/10/142725-374b5192-9d08-4427-99d4-e68a5121c5d3.jpeg"
              alt="P.S. 155 students engaged in classroom learning"
              className="rounded-2xl shadow-xl w-full object-cover transform hover:scale-[1.01] transition-transform duration-500"
            />
            {/* Decorative Elements */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-school-gold/20 rounded-full -z-10" />
            <div className="absolute -top-6 -left-6 w-24 h-24 border-4 border-school-blue rounded-2xl -z-10" />
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <span className="text-school-gold-dark font-bold uppercase tracking-wider mb-2 sm:mb-3 text-xs sm:text-sm block">Our Mission</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-school-blue mb-6 sm:mb-8 leading-tight">
            Cultivating Curiosity & Community
          </h2>
          <p className="text-base sm:text-lg text-neutral-600 mb-4 sm:mb-6 leading-relaxed">
            At P.S. 155, we believe that every child brings a unique brilliance to our classrooms.
            Our mission is to foster an inclusive environment where students feel valued, supported,
            and excited to learn.
          </p>
          <p className="text-base sm:text-lg text-neutral-600 mb-8 sm:mb-10 leading-relaxed">
            Grounded in empathy and equity, we partner with families to ensure academic excellence
            and social-emotional growth for every student in East Harlem.
          </p>

          <div className="flex flex-col gap-4">
             <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-neutral-100 hover:border-school-gold/30 transition-colors">
                <div className="p-2 bg-blue-50 rounded-lg text-school-blue">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                </div>
                <div>
                    <h3 className="font-bold text-school-blue text-lg">Academic Excellence</h3>
                    <p className="text-neutral-500 text-sm">Rigorous instruction tailored to every learner's needs.</p>
                </div>
             </div>

             <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-neutral-100 hover:border-school-gold/30 transition-colors">
                <div className="p-2 bg-amber-50 rounded-lg text-school-gold-dark">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                </div>
                <div>
                    <h3 className="font-bold text-school-blue text-lg">Emotional Growth</h3>
                    <p className="text-neutral-500 text-sm">Nurturing empathy, resilience, and self-confidence.</p>
                </div>
             </div>

             <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-neutral-100 hover:border-school-gold/30 transition-colors">
                <div className="p-2 bg-green-50 rounded-lg text-green-600">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                </div>
                <div>
                    <h3 className="font-bold text-school-blue text-lg">Inclusive Community</h3>
                    <p className="text-neutral-500 text-sm">A safe space where every family belongs.</p>
                </div>
             </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================
   STATS SECTION
   ============================================ */
const StatsSection = () => {
  const stats = [
    { value: '99%', label: 'Families feel included' },
    { value: '96%', label: 'Teacher Engagement' },
    { value: '100%', label: 'Relationship Building' },
  ];

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-school-blue-dark text-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid sm:grid-cols-3 gap-8 sm:gap-12 text-center divide-y sm:divide-y-0 sm:divide-x divide-white/10">
          {stats.map((stat, idx) => (
            <div key={idx} className="pt-6 sm:pt-0 first:pt-0 px-4 group">
              <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-school-gold-on-blue mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">{stat.value}</div>
              <div className="text-sm sm:text-base md:text-lg font-medium tracking-wider sm:tracking-widest uppercase opacity-80">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================
   NEWS & EVENTS
   ============================================ */
const NewsEventsSection = () => {
  // Get next 3 upcoming events from calendar data
  const getUpcomingEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for accurate comparison

    return events
      .filter(event => event.date >= today)
      .sort((a, b) => a.date - b.date)
      .slice(0, 3)
      .map(event => ({
        date: event.date.getDate().toString(),
        month: event.date.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
        title: event.title,
        desc: event.description || 'All Day Event'
      }));
  };

  const upcomingEvents = getUpcomingEvents();

  return (
    <section className="py-16 sm:py-20 md:py-28 bg-white px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <span className="text-school-gold-dark font-bold uppercase tracking-wider text-xs sm:text-sm">Stay Updated</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-school-blue mt-2 mb-4 sm:mb-6">Happening at P.S. 155</h2>
          <div className="w-20 sm:w-24 h-1.5 bg-school-gold mx-auto rounded-full" />
        </div>

        <div className="grid lg:grid-cols-12 gap-6 sm:gap-8">
            {/* News Column */}
            <div className="lg:col-span-4 bg-neutral-50 rounded-2xl p-8 border border-neutral-100 hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-school-blue mb-8 flex items-center gap-3">
                    <span className="p-2 bg-white rounded-lg shadow-sm text-school-gold-dark">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>
                    </span>
                    Latest News
                </h3>
                <div className="space-y-8">
                    <div className="group cursor-pointer">
                        <span className="text-xs font-bold text-school-gold-dark uppercase tracking-wider bg-school-gold/10 px-2 py-1 rounded">Announcement</span>
                        <h4 className="text-xl font-bold text-neutral-800 mt-3 mb-2 group-hover:text-school-blue transition-colors">Welcome Back to School!</h4>
                        <p className="text-neutral-600 text-sm leading-relaxed line-clamp-2">We are excited to welcome all students back for the 2025-2026 school year. Check your email for supply lists.</p>
                    </div>
                    <div className="w-full h-px bg-neutral-200" />
                    <div className="group cursor-pointer">
                         <span className="text-xs font-bold text-school-blue uppercase tracking-wider bg-blue-100 px-2 py-1 rounded">Update</span>
                         <h4 className="text-xl font-bold text-neutral-800 mt-3 mb-2 group-hover:text-school-blue transition-colors">New Parent Portal Features</h4>
                         <p className="text-neutral-600 text-sm leading-relaxed line-clamp-2">Check out the new features available in our parent portal for tracking progress and attendance.</p>
                    </div>
                </div>
            </div>

            {/* Events Column */}
            <div className="lg:col-span-8 bg-white rounded-2xl shadow-xl shadow-neutral-100 border border-neutral-100 p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-3 sm:gap-4">
                    <h3 className="text-2xl font-bold text-school-blue flex items-center gap-3">
                        <span className="p-2 bg-blue-50 rounded-lg shadow-sm text-school-blue">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        </span>
                        Upcoming Events
                    </h3>
                    <a href="/calendar" className="text-sm font-semibold text-neutral-500 hover:text-school-blue transition-colors">View Full Calendar →</a>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                    {upcomingEvents.length > 0 ? (
                      upcomingEvents.map((ev, i) => (
                        <div key={i} className="flex gap-3 sm:gap-5 p-3 sm:p-4 rounded-xl hover:bg-neutral-50 transition-colors cursor-pointer border border-transparent hover:border-neutral-100 group">
                            <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-2xl flex flex-col items-center justify-center border border-neutral-200 shadow-sm group-hover:border-school-gold group-hover:shadow-md transition-all">
                                <span className="text-school-gold-dark font-bold text-xs sm:text-sm uppercase tracking-wide">{ev.month}</span>
                                <span className="text-school-blue font-black text-2xl sm:text-3xl leading-none">{ev.date}</span>
                            </div>
                            <div className="flex flex-col justify-center">
                                <h4 className="text-base sm:text-lg font-bold text-neutral-800 group-hover:text-school-blue transition-colors leading-tight mb-1 sm:mb-2">{ev.title}</h4>
                                <p className="text-neutral-500 text-xs sm:text-sm">{ev.desc}</p>
                            </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-2 text-center py-8 text-neutral-500">
                        No upcoming events. Check the calendar for the full schedule.
                      </div>
                    )}
                </div>
            </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================
   INSTAGRAM SECTION
   ============================================ */
const InstagramSection = () => {
  const posts = [
    { img: 'https://3.files.edl.io/f1a0/25/03/06/153130-4ff331c1-ccf6-45c7-b911-3a80a604bb1c.jpg', caption: 'Learning together!' },
    { img: 'https://3.files.edl.io/70ca/25/02/12/212017-3bb265ce-9203-4065-a52b-7830547a6974.png', caption: 'Classroom moments' },
    { img: 'https://3.files.edl.io/d6c4/25/02/12/212059-14c8bcd2-e48f-4d89-8ea5-84523aed39ae.png', caption: 'Reading buddies' },
    { img: 'https://3.files.edl.io/6f24/22/03/22/161414-a9b6960d-de73-4da2-864f-5230fbe390cd.JPG', caption: 'School spirit' }
  ];

  return (
    <section className="py-16 sm:py-20 md:py-24 overflow-hidden bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 sm:mb-10 md:mb-12 gap-4 sm:gap-6">
            <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-school-blue mb-2 sm:mb-3">Life at P.S. 155</h2>
                <p className="text-neutral-500 text-base sm:text-lg">Follow our journey on Instagram <span className="font-semibold text-school-gold-dark">@ps155d4</span></p>
            </div>
            <a
                href="https://instagram.com/ps155d4"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-2.5 sm:py-3 bg-white text-school-blue border border-school-blue rounded-full font-bold text-sm sm:text-base hover:bg-school-blue hover:text-white transition-all duration-300 shadow-sm hover:shadow-md whitespace-nowrap"
            >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                View on Instagram
            </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {posts.map((post, idx) => (
                <div key={idx} className="group relative aspect-square overflow-hidden rounded-xl sm:rounded-2xl cursor-pointer shadow-md">
                    <img src={post.img} alt={post.caption} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-school-blue-dark opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4 sm:p-6">
                        <p className="text-white font-bold text-center text-sm sm:text-base translate-y-4 group-hover:translate-y-0 transition-transform duration-300">{post.caption}</p>
                    </div>
                </div>
            ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================
   CALL TO ACTION
   ============================================ */
const CallToActionSection = () => {
    return (
        <section className="py-20 sm:py-24 md:py-32 bg-school-gold relative overflow-hidden">
             <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10 text-center">
                 <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-school-blue mb-6 sm:mb-8 drop-shadow-md leading-tight">Join the P.S. 155 Family</h2>
                 <p className="text-lg sm:text-xl md:text-2xl text-school-blue-dark mb-8 sm:mb-10 md:mb-12 leading-relaxed font-medium px-4">We are currently accepting enrollments for the upcoming school year. <br className="hidden sm:block"/>Come see why our families love P.S. 155!</p>
                 <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
                     <a href="#enroll" className="px-8 sm:px-10 py-4 sm:py-5 bg-school-blue text-white font-bold text-lg sm:text-xl rounded-full shadow-xl hover:bg-blue-800 hover:scale-105 transition-all duration-300">Start Enrollment</a>
                     <a href="/contact" className="px-8 sm:px-10 py-4 sm:py-5 bg-school-blue text-white font-bold text-lg sm:text-xl rounded-full shadow-xl hover:bg-blue-800 hover:scale-105 transition-all duration-300">Contact Us</a>
                 </div>
             </div>
        </section>
    );
};

export default HomePage;
